import {
    DeleteOutlined,
    EditOutlined,
    EllipsisOutlined,
    NodeIndexOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { Dropdown, Tooltip } from 'antd';
import classNames from 'classnames';
import styles from './NodeOps.module.scss';

const stop = (event) => event.stopPropagation();

/**
 * 「+」按钮：有子级可插（章、节）时弹菜单选同级 / 子级；末级直接添加同级。
 * Tooltip 放在 Dropdown 内层包住按钮（antd Dropdown 不会把触发事件透传给外层 Tooltip）。
 */
function AddNodeButton({ siblingLabel, childLabel, onAddSibling, onAddChild }) {
    if (!onAddChild) {
        return (
            <Tooltip title={siblingLabel}>
                <button
                    type="button"
                    className={styles.opBtn}
                    aria-label={siblingLabel}
                    onClick={onAddSibling}
                >
                    <PlusOutlined />
                </button>
            </Tooltip>
        );
    }
    const menu = {
        items: [
            { key: 'sibling', icon: <PlusOutlined />, label: siblingLabel },
            { key: 'child', icon: <NodeIndexOutlined />, label: childLabel },
        ],
        onClick: ({ key, domEvent }) => {
            domEvent.stopPropagation();
            if (key === 'sibling') onAddSibling();
            else if (key === 'child') onAddChild();
        },
    };
    return (
        <Dropdown menu={menu} trigger={['click']} placement="bottomRight">
            <Tooltip title="添加">
                <button type="button" className={styles.opBtn} aria-label="添加">
                    <PlusOutlined />
                </button>
            </Tooltip>
        </Dropdown>
    );
}

/**
 * 章 / 节 / 小节的编辑、添加、删除。
 * 桌面：三个图标按钮平铺；手机（≤768px，由样式切换）：收成一个「⋯」按钮，点开菜单选操作，
 * 行里只占一个按钮的位置，标题能用满宽度。
 */
export default function NodeOps({
    onEdit,
    onDelete,
    siblingLabel,
    childLabel,
    onAddSibling,
    onAddChild,
    className,
}) {
    const moreMenu = {
        items: [
            { key: 'edit', icon: <EditOutlined />, label: '编辑' },
            { key: 'sibling', icon: <PlusOutlined />, label: siblingLabel },
            onAddChild ? { key: 'child', icon: <NodeIndexOutlined />, label: childLabel } : null,
            { type: 'divider' },
            { key: 'delete', icon: <DeleteOutlined />, label: '删除', danger: true },
        ].filter(Boolean),
        onClick: ({ key, domEvent }) => {
            domEvent.stopPropagation();
            if (key === 'edit') onEdit();
            else if (key === 'sibling') onAddSibling();
            else if (key === 'child') onAddChild?.();
            else if (key === 'delete') onDelete();
        },
    };

    return (
        <div className={classNames(styles.ops, className)} onClick={stop}>
            <div className={styles.desktopOps}>
                <Tooltip title="编辑">
                    <button
                        type="button"
                        className={styles.opBtn}
                        aria-label="编辑"
                        onClick={onEdit}
                    >
                        <EditOutlined />
                    </button>
                </Tooltip>
                <AddNodeButton
                    siblingLabel={siblingLabel}
                    childLabel={childLabel}
                    onAddSibling={onAddSibling}
                    onAddChild={onAddChild}
                />
                <Tooltip title="删除">
                    <button
                        type="button"
                        className={classNames(styles.opBtn, styles.danger)}
                        aria-label="删除"
                        onClick={onDelete}
                    >
                        <DeleteOutlined />
                    </button>
                </Tooltip>
            </div>
            <Dropdown menu={moreMenu} trigger={['click']} placement="bottomRight">
                <button
                    type="button"
                    className={classNames(styles.opBtn, styles.moreBtn)}
                    aria-label="更多操作"
                >
                    <EllipsisOutlined />
                </button>
            </Dropdown>
        </div>
    );
}
